import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, X, Search, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';

interface AnalyticsFilterPanelProps {
  dateRange?: DateRange;
  onDateRangeChange: (range: DateRange | undefined) => void;
  prequalStatus?: string;
  onPrequalStatusChange: (status: string) => void;
  searchQuery?: string;
  onSearchQueryChange: (query: string) => void;
  ratingRange?: [number, number];
  onRatingRangeChange: (range: [number, number]) => void;
  onClearFilters: () => void;
  activeFilterCount: number;
}

export function AnalyticsFilterPanel({
  dateRange,
  onDateRangeChange,
  prequalStatus,
  onPrequalStatusChange,
  searchQuery = '',
  onSearchQueryChange,
  ratingRange = [0, 5],
  onRatingRangeChange,
  onClearFilters,
  activeFilterCount,
}: AnalyticsFilterPanelProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <span className="text-sm font-medium">Filters:</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary">{activeFilterCount} active</Badge>
          )}
        </div>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contractors..."
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            className="pl-8"
          />
        </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-[280px] justify-start text-left font-normal">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "LLL dd, y")} -{" "}
                  {format(dateRange.to, "LLL dd, y")}
                </>
              ) : (
                format(dateRange.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={onDateRangeChange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>

        <Select value={prequalStatus} onValueChange={onPrequalStatusChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Prequalification Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          <X className="h-4 w-4 mr-1" />
          Clear All
        </Button>
      </div>

      <div className="p-4 bg-muted/50 rounded-lg">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Rating Range</span>
            <span className="text-sm text-muted-foreground">
              {ratingRange[0].toFixed(1)} - {ratingRange[1].toFixed(1)} stars
            </span>
          </div>
          <Slider
            value={ratingRange}
            onValueChange={(value) => onRatingRangeChange(value as [number, number])}
            min={0}
            max={5}
            step={0.5}
            minStepsBetweenThumbs={0.5}
          />
        </div>
      </div>
    </div>
  );
}
